import { useRef, type ReactNode } from 'react';
import { useDialogA11y } from './useDialogA11y';

/**
 * A centered confirmation modal (epic UX: destructive actions confirm via a
 * MODAL, never a drawer — Story 2.4 UX-DR11). Mirrors {@link Drawer}'s overlay
 * a11y mechanics via the shared `useDialogA11y` hook — a fixed scrim closes on
 * click, Esc closes, the panel receives focus on open, focus is restored to the
 * opener on close, and Tab is trapped within the dialog (Story 4.4) — but
 * centered rather than right-aligned. `role="dialog"` + `aria-modal="true"`. Used for the
 * logged, history-bearing Device removal (a Vendor un-assignment stays a
 * lighter-weight inline confirm — see Story 2.3 Design Notes).
 *
 * While the mutation is in flight (`busy`) the Confirm button is disabled so a
 * double-click cannot fire duplicate requests.
 */
export function ConfirmModal({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmTone = 'bad',
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Tone of the confirm button. Defaults to `bad` because every caller until DEV-4424
   * confirmed a DESTRUCTIVE act, and red is the right warning for those.
   *
   * Reactivating a Site is the first RESTORATIVE confirm in the app — it puts a vessel
   * back, removes nothing, and is the recovery from the destructive action. Painting it in
   * the same red as "Remove device" would tell the user to hesitate over the safe half of
   * a reversible pair. Opt-in, so no existing caller changes.
   */
  confirmTone?: 'bad' | 'ok';
  busy?: boolean;
  /**
   * A failure message from the confirmed action. Rendered inside the dialog so
   * it is visible while the modal is still open — the section behind the scrim
   * is not (never silently succeed / surface errors inline, Story 2.4).
   */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const panelRef = useRef<HTMLElement>(null);
  // Esc maps to Cancel for a confirm modal (the non-destructive dismissal).
  useDialogA11y(panelRef, onCancel);

  return (
    <div
      role="presentation"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--scrim)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 'var(--z-modal)' as unknown as number,
      }}
    >
      <section
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="card stack"
        style={{
          width: 'min(440px, 100%)',
          boxShadow: 'var(--elevation-modal)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{title}</h2>
        <div style={{ margin: 0, color: 'var(--text-2)', fontSize: 14 }}>{message}</div>
        <div role="alert" aria-live="assertive" style={{ minHeight: error ? undefined : 0 }}>
          {error && (
            <span className="status-pill" data-tone="bad">
              <span className="status-pill__dot" aria-hidden="true" />
              {error}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="icon-button" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            data-tone={confirmTone}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </section>
    </div>
  );
}
